import React from 'react';
import QRCode from 'qrcode';
import { AuthContext } from '../../../context/Auth/Auth';
import Spinner from '../../../components/Spinner/Spinner';
import {
  totpSetupService,
  totpConfirmService,
  regenerateRecoveryCodesService,
} from '../../../services/Recovery/Recovery';

type Step = 'idle' | 'awaiting-confirm' | 'enabled';

const AccountSecurity = ({ showModalError }: { showModalError: (message: string) => void }) => {
  const { token } = React.useContext(AuthContext);
  const [step, setStep] = React.useState<Step>('idle');
  const [qrDataUrl, setQrDataUrl] = React.useState('');
  const [manualSecret, setManualSecret] = React.useState('');
  const [code, setCode] = React.useState('');
  const [recoveryCodes, setRecoveryCodes] = React.useState<string[]>([]);
  const [submitting, setSubmitting] = React.useState(false);

  const startSetup = React.useCallback(async () => {
    setSubmitting(true);
    try {
      const { secret, otpauthUrl } = await totpSetupService(token);
      setManualSecret(secret);
      setQrDataUrl(await QRCode.toDataURL(otpauthUrl));
      setStep('awaiting-confirm');
    } catch (error) {
      if (error instanceof Error) showModalError(error.message);
    } finally {
      setSubmitting(false);
    }
  }, [token, showModalError]);

  const confirmSetup = React.useCallback(async () => {
    if (!code) return;
    setSubmitting(true);
    try {
      const { recoveryCodes } = await totpConfirmService(token, code);
      setRecoveryCodes(recoveryCodes);
      setStep('enabled');
    } catch (error) {
      if (error instanceof Error) showModalError(error.message);
    } finally {
      setSubmitting(false);
    }
  }, [token, code, showModalError]);

  const regenerateCodes = React.useCallback(async () => {
    setSubmitting(true);
    try {
      const { recoveryCodes } = await regenerateRecoveryCodesService(token);
      setRecoveryCodes(recoveryCodes);
    } catch (error) {
      if (error instanceof Error) showModalError(error.message);
    } finally {
      setSubmitting(false);
    }
  }, [token, showModalError]);

  const copyCodes = React.useCallback(() => {
    navigator.clipboard?.writeText(recoveryCodes.join('\n')).catch(() => undefined);
  }, [recoveryCodes]);

  const downloadCodes = React.useCallback(() => {
    const blob = new Blob([recoveryCodes.join('\n') + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lis-codigos-recuperacion.txt';
    a.click();
    URL.revokeObjectURL(url);
  }, [recoveryCodes]);

  return (
    <section className="lis-container" style={{ padding: '2rem 0', maxWidth: 560 }}>
      <h1 className="form-title">Seguridad de la cuenta</h1>
      <p className="form-description">
        Configura Microsoft Authenticator para proteger tu cuenta y poder recuperarla sin depender del correo.
      </p>

      {step === 'idle' && (
        <button className="form-button" type="button" disabled={submitting} onClick={startSetup}>
          {submitting ? <><Spinner label="Generando" /><span>Generando…</span></> : 'Configurar Microsoft Authenticator'}
        </button>
      )}

      {step === 'awaiting-confirm' && (
        <React.Fragment>
          {qrDataUrl && <img src={qrDataUrl} alt="Código QR para configurar Microsoft Authenticator" width={240} height={240} />}
          <p className="form-support">¿No puedes escanear el QR? Ingresa este código manualmente en tu app: <code>{manualSecret}</code></p>
          <label className="form-label" htmlFor="totp-confirm-code">Código de 6 dígitos</label>
          <input
            className="form-input"
            id="totp-confirm-code"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <button className="form-button" type="button" disabled={submitting} onClick={confirmSetup}>
            {submitting ? <><Spinner label="Confirmando" /><span>Confirmando…</span></> : 'Confirmar y activar'}
          </button>
        </React.Fragment>
      )}

      {step === 'enabled' && (
        <React.Fragment>
        <p className="form-support">Microsoft Authenticator activado. Guarda estos códigos de recuperación de un solo uso — no volverán a mostrarse.</p>
          <ul className="recovery-codes-list">
            {recoveryCodes.map((c) => <li key={c}>{c}</li>)}
          </ul>
          <div className="recovery-codes-actions">
            <button className="form-button" type="button" onClick={copyCodes}>Copiar</button>
            <button className="form-button" type="button" onClick={downloadCodes}>Descargar</button>
          </div>
          <p className="form-support">¿Perdiste tus códigos? Puedes regenerarlos, lo que invalida los anteriores.</p>
          <button className="form-button" type="button" disabled={submitting} onClick={regenerateCodes}>
            {submitting ? <><Spinner label="Regenerando" /><span>Regenerando…</span></> : 'Regenerar códigos'}
          </button>
        </React.Fragment>
      )}
    </section>
  );
};

export default AccountSecurity;
